// @ts-nocheck
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target, 
  Calendar, 
  AlertTriangle,
  PieChart,
  BarChart3
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { entryCountsInOperationalFinance } from "@/utils/financialOperationalImpact";

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  payment_notes?: string;
}

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number;
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface FinancialKPIsProps {
  entries: FinancialEntry[];
  recurringEntries: RecurringEntry[];
}

const FinancialKPIs = ({ entries, recurringEntries }: FinancialKPIsProps) => {
  const [allEntries, setAllEntries] = useState<FinancialEntry[]>([]);
  // Função para gerar entradas virtuais baseadas nas recorrentes
  const generateVirtualEntries = async () => {
    const virtualEntries: FinancialEntry[] = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // Mês 1-12
    const currentYear = currentDate.getFullYear();

    // Buscar pagamentos existentes para o mês atual
    const { data: existingPayments } = await supabase
      .from('recurring_payments')
      .select('recurring_entry_id')
      .eq('payment_month', currentMonth)
      .eq('payment_year', currentYear);

    const paidRecurringIds = new Set(existingPayments?.map(p => p.recurring_entry_id) || []);

    recurringEntries
      .filter(entry => entry.is_active && !paidRecurringIds.has(entry.id))
      .forEach(entry => {
        // Criar entrada virtual para o mês atual
        const virtualEntry: FinancialEntry = {
          id: `virtual-${entry.id}-${currentMonth - 1}-${currentYear}`, // currentMonth - 1 para manter compatibilidade com getMonth()
          description: `${entry.description} (Recorrente)`,
          amount: entry.amount,
          entry_type: entry.entry_type,
          category: entry.category,
          status: 'Pendente', // Entradas recorrentes são consideradas pendentes até confirmadas
          created_at: new Date(currentYear, currentMonth - 1, entry.due_day).toISOString(),
          clients: entry.clients,
          cases: entry.cases
        };
        virtualEntries.push(virtualEntry);
      });

    return virtualEntries;
  };

  // Função para atualizar as entradas combinadas
  const updateAllEntries = async () => {
    const virtualEntries = await generateVirtualEntries();
    const operational = entries.filter(entryCountsInOperationalFinance);
    setAllEntries([...operational, ...virtualEntries]);
  };

  // Atualizar entradas combinadas quando entries ou recurringEntries mudarem
  useEffect(() => {
    if (entries.length > 0 || recurringEntries.length > 0) {
      updateAllEntries();
    }
  }, [entries, recurringEntries]);

  const calculateKPIs = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    // Mês de referência: data do pagamento (realizado ou previsto); sem data, usa o cadastro
    const referenceDate = (entry: any) => {
      const raw = entry.actual_payment_date || entry.payment_date || entry.created_at;
      return new Date(String(raw).length === 10 ? `${raw}T12:00:00` : raw);
    };
    const currentMonthEntries = allEntries.filter(entry => {
      const entryDate = referenceDate(entry);
      return entryDate.getMonth() === currentMonth && entryDate.getFullYear() === currentYear;
    });

    const lastMonthEntries = allEntries.filter(entry => {
      const entryDate = referenceDate(entry);
      return entryDate.getMonth() === lastMonth && entryDate.getFullYear() === lastMonthYear;
    });

    const paidEntries = allEntries.filter(entry => entry.status === 'Pago');
    const pendingEntries = allEntries.filter(entry => entry.status === 'Pendente');
    const overdueEntries = allEntries.filter(entry => {
      if (entry.payment_date && entry.status !== 'Pago') {
        return new Date(entry.payment_date) < currentDate;
      }
      return false;
    });

    // Receitas e despesas
    const revenues = allEntries.filter(e => e.entry_type === 'receita');
    const expenses = allEntries.filter(e => e.entry_type === 'despesa');
    
    const currentMonthRevenues = currentMonthEntries.filter(e => e.entry_type === 'receita');
    const currentMonthExpenses = currentMonthEntries.filter(e => e.entry_type === 'despesa');
    const lastMonthRevenues = lastMonthEntries.filter(e => e.entry_type === 'receita');
    const lastMonthExpenses = lastMonthEntries.filter(e => e.entry_type === 'despesa');

    // Totais
    const totalRevenue = revenues.reduce((sum, entry) => sum + entry.amount, 0);
    const totalExpense = expenses.reduce((sum, entry) => sum + entry.amount, 0);
    const netProfit = totalRevenue - totalExpense;
    
    const currentMonthRevenue = currentMonthRevenues.reduce((sum, entry) => sum + entry.amount, 0);
    const currentMonthExpense = currentMonthExpenses.reduce((sum, entry) => sum + entry.amount, 0);
    const currentMonthNetProfit = currentMonthRevenue - currentMonthExpense;
    const lastMonthRevenue = lastMonthRevenues.reduce((sum, entry) => sum + entry.amount, 0);
    const lastMonthExpense = lastMonthExpenses.reduce((sum, entry) => sum + entry.amount, 0);

    // Valores pendentes e vencidos
    const pendingAmount = pendingEntries.reduce((sum, entry) => sum + entry.amount, 0);
    const overdueAmount = overdueEntries.reduce((sum, entry) => sum + entry.amount, 0);

    // Taxa de crescimento
    const revenueGrowth = lastMonthRevenue > 0 ? 
      ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100) : 0;
    
    // Margem de lucro
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue * 100) : 0;

    // Taxa de conversão (receitas pagas vs total)
    const paidRevenues = paidEntries.filter(e => e.entry_type === 'receita');
    const conversionRate = revenues.length > 0 ? 
      (paidRevenues.length / revenues.length * 100) : 0;

    // Tempo médio para recebimento
    // Ignora lançamentos cadastrados depois de já pagos (dariam tempo negativo)
    const collectionDays = paidRevenues
      .map(entry => {
        const paidRaw = (entry as any).actual_payment_date || entry.payment_date;
        if (!paidRaw) return null;
        const days = (new Date(paidRaw).getTime() - new Date(entry.created_at).getTime()) / (1000 * 60 * 60 * 24);
        return days >= 0 ? days : null;
      })
      .filter((d): d is number => d !== null);
    const avgCollectionTime = collectionDays.length > 0
      ? collectionDays.reduce((sum, d) => sum + d, 0) / collectionDays.length
      : 0;

    return {
      totalRevenue,
      totalExpense,
      netProfit,
      currentMonthRevenue,
      currentMonthExpense,
      currentMonthNetProfit,
      revenueGrowth,
      profitMargin,
      conversionRate,
      pendingAmount,
      overdueAmount,
      avgCollectionTime,
      hasCollectionData: collectionDays.length > 0,
      lastMonthRevenue,
      pendingCount: pendingEntries.length,
      overdueCount: overdueEntries.length
    };
  };

  const kpis = calculateKPIs();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getGrowthColor = (value: number) => {
    if (value > 0) return "text-tertiary";
    if (value < 0) return "text-destructive";
    return "text-muted-foreground";
  };

  const getGrowthIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="w-4 h-4" />;
    if (value < 0) return <TrendingDown className="w-4 h-4" />;
    return <DollarSign className="w-4 h-4" />;
  };

  const tone = (value: number, positive = 'text-emerald-600', negative = 'text-red-600') =>
    value > 0 ? positive : value < 0 ? negative : 'text-slate-300';
  const marginLabel = kpis.profitMargin >= 20 ? 'Excelente' : kpis.profitMargin >= 10 ? 'Boa' : 'Atenção';
  const marginStyle = kpis.profitMargin >= 20 ? 'bg-emerald-50 text-emerald-700' : kpis.profitMargin >= 10 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';

  const Tile = ({ label, value, hint, valueClass = 'text-slate-900', extra }: { label: string; value: string; hint: string; valueClass?: string; extra?: React.ReactNode }) => (
    <div className="bg-white px-5 py-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${valueClass}`}>{value}</p>
      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
        <span>{hint}</span>
        {extra}
      </div>
    </div>
  );

  const Section = ({ eyebrow, title, children, cols = 'sm:grid-cols-4' }: { eyebrow: string; title: string; children: React.ReactNode; cols?: string }) => (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-5 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">{eyebrow}</p>
        <p className="mt-0.5 text-lg font-semibold capitalize tracking-tight text-slate-900">{title}</p>
      </div>
      <div className={`grid grid-cols-1 gap-px bg-slate-100 ${cols}`}>{children}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <Section eyebrow="Este mês" title={new Date().toLocaleDateString('pt-BR', { month: 'long' })}>
        <Tile label="Receitas" value={formatCurrency(kpis.currentMonthRevenue)} hint="recebidas e previstas" valueClass={tone(kpis.currentMonthRevenue)} />
        <Tile label="Despesas" value={formatCurrency(kpis.currentMonthExpense)} hint="pagas e previstas" valueClass={kpis.currentMonthExpense > 0 ? 'text-red-600' : 'text-slate-300'} />
        <Tile label="Resultado" value={formatCurrency(kpis.currentMonthNetProfit)} hint={kpis.currentMonthNetProfit >= 0 ? 'sobra no mês' : 'falta no mês'} valueClass={tone(kpis.currentMonthNetProfit)} />
        <Tile
          label="Vs. mês anterior"
          value={kpis.lastMonthRevenue > 0 ? `${kpis.revenueGrowth > 0 ? '+' : ''}${formatPercentage(kpis.revenueGrowth)}` : '—'}
          hint={kpis.lastMonthRevenue > 0 ? `antes: ${formatCurrency(kpis.lastMonthRevenue)}` : 'sem receitas no mês anterior'}
          valueClass={kpis.lastMonthRevenue > 0 ? tone(kpis.revenueGrowth) : 'text-slate-300'}
        />
      </Section>

      <Section eyebrow="A receber" title="Pendências" cols="sm:grid-cols-2">
        <Tile label="Pendentes" value={formatCurrency(kpis.pendingAmount)} hint={`${kpis.pendingCount} ${kpis.pendingCount === 1 ? 'lançamento aguardando' : 'lançamentos aguardando'}`} valueClass={kpis.pendingAmount > 0 ? 'text-amber-600' : 'text-slate-300'} />
        <Tile label="Vencidos" value={formatCurrency(kpis.overdueAmount)} hint={`${kpis.overdueCount} ${kpis.overdueCount === 1 ? 'lançamento vencido' : 'lançamentos vencidos'}`} valueClass={kpis.overdueAmount > 0 ? 'text-red-600' : 'text-slate-300'} />
      </Section>

      <Section eyebrow="Desempenho geral" title="Todo o período" cols="sm:grid-cols-3">
        <Tile
          label="Margem de lucro"
          value={formatPercentage(kpis.profitMargin)}
          hint={`lucro de ${formatCurrency(kpis.netProfit)}`}
          extra={<span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${marginStyle}`}>{marginLabel}</span>}
        />
        <Tile label="Taxa de recebimento" value={formatPercentage(kpis.conversionRate)} hint="receitas já pagas" />
        <Tile
          label="Tempo médio de recebimento"
          value={kpis.hasCollectionData ? `${Math.round(kpis.avgCollectionTime)} ${Math.round(kpis.avgCollectionTime) === 1 ? 'dia' : 'dias'}` : '—'}
          hint="do cadastro ao pagamento"
          valueClass={kpis.hasCollectionData ? 'text-slate-900' : 'text-slate-300'}
        />
      </Section>
    </div>
  );
};

export default FinancialKPIs;